/**
 * RequestCleanupUtility - Utility for cleaning up pending requests
 * 
 * Extracted from TypesetterWorkerClient for better modularity and testability.
 * 
 * This utility provides functions for rejecting and clearing pending requests
 * when operations are cancelled or workers are terminated.
 */

/**
 * Error message for terminated worker
 */
export const WORKER_TERMINATED_ERROR = 'Worker terminated';

/**
 * Reject all pending requests in a map
 * 
 * Iterates through a map of pending requests and rejects each one with
 * the specified error message, then clears the map.
 * 
 * @param pendingRequests - Map of pending requests with reject callbacks
 * @param errorMessage - Error message to use for rejection (default: WORKER_TERMINATED_ERROR)
 */
export function rejectAllPendingRequests<T extends { reject: (error: Error) => void }>(
  pendingRequests: Map<string | number, T>,
  errorMessage: string = WORKER_TERMINATED_ERROR
): void {
  for (const [, pending] of pendingRequests) {
    pending.reject(new Error(errorMessage));
  }
  pendingRequests.clear();
}

/**
 * Reject all reactive requests with timeout cleanup
 * 
 * Iterates through a map of reactive requests, clears their timeouts,
 * rejects them with the specified error message, then clears the map.
 * 
 * @param reactiveRequests - Map of reactive requests with timeout and reject callbacks
 * @param errorMessage - Error message to use for rejection (default: WORKER_TERMINATED_ERROR)
 */
export function rejectAllReactiveRequests<T extends {
  reject: (error: Error) => void;
  timeout: NodeJS.Timeout;
}>(
  reactiveRequests: Map<string | number, T>,
  errorMessage: string = WORKER_TERMINATED_ERROR
): void {
  for (const [, request] of reactiveRequests) {
    clearTimeout(request.timeout);
    request.reject(new Error(errorMessage));
  }
  reactiveRequests.clear();
}

/**
 * Cleanup all pending and reactive requests
 * 
 * Convenience function that rejects both pending and reactive requests.
 * 
 * @param pendingRequests - Map of pending requests
 * @param reactiveRequests - Map of reactive requests
 * @param errorMessage - Error message to use for rejection (default: WORKER_TERMINATED_ERROR)
 */
export function cleanupAllRequests<
  TPending extends { reject: (error: Error) => void },
  TReactive extends {
    reject: (error: Error) => void;
    timeout: NodeJS.Timeout;
  }
>(
  pendingRequests: Map<string | number, TPending>,
  reactiveRequests: Map<string | number, TReactive>,
  errorMessage: string = WORKER_TERMINATED_ERROR
): void {
  rejectAllPendingRequests(pendingRequests, errorMessage);
  rejectAllReactiveRequests(reactiveRequests, errorMessage);
}

